import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { announce } from '@theme/ca-announcer';
import {
  createShare,
  guestItems,
  isLoggedIn,
  markLoggedOut,
  proxyBase,
  renameList,
  sanitizeListName,
  totalCount,
} from '@theme/wishlist';

const PAGE_SIZE = 20;

/** A missing key renders as "Translation missing: …", which is truthy and would defeat a `||`. */
const translated = (value) => (value && !value.startsWith('Translation missing') ? value : '');

const getMount = () => document.querySelector('[data-wishlist-mount]');

const pageSize = () => Number(getMount()?.dataset.pageSize) || PAGE_SIZE;

const guestHandles = () =>
  guestItems()
    .map((item) => `${item.h}:${item.vid ?? ''}`)
    .slice(0, pageSize());

let inFlight = null;

async function loadPanel({ listId, page, handles, prefetched } = {}) {
  const mount = getMount();
  if (!mount) return false;

  inFlight?.abort();
  const controller = new AbortController();
  inFlight = controller;

  const url = new URL(`${proxyBase()}/view`, window.location.origin);
  if (listId) url.searchParams.set('list', listId);
  if (page) url.searchParams.set('page', String(page));
  if (handles?.length) url.searchParams.set('handles', handles.join(','));
  if (mount.dataset.pageSize) url.searchParams.set('size', mount.dataset.pageSize);

  mount.setAttribute('aria-busy', 'true');
  try {
    const markup = prefetched
      ? await prefetched
      : await fetch(url, { credentials: 'same-origin', signal: controller.signal }).then((response) => {
          if (!response.ok) throw new Error(`view_http_${response.status}`);
          return response.text();
        });
    if (controller.signal.aborted) return 'aborted';

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const panel = doc.querySelector('[data-wishlist-panel]');
    if (!panel) throw new Error('view_panel_missing');

    const restoreFocus = mount.contains(document.activeElement);
    mount.replaceChildren(...panel.childNodes);
    mount.dataset.listId = panel.dataset.listId || mount.dataset.listId || '';
    mount.dataset.page = panel.dataset.page ?? '1';
    mount.dataset.lastPage = panel.dataset.lastPage ?? mount.dataset.page;

    if (restoreFocus) {
      // Skip the reorder controls: they say nothing about where the shopper landed.
      const pager = mount.querySelector('[data-wishlist-page]');
      const fallback = mount.querySelector(
        'a, button:not(.ca-wishlist__handle):not(.ca-wishlist__move-button)'
      );
      (pager ?? fallback)?.focus({ preventScroll: true });
    }
    setToolbarVisible(!!mount.querySelector('[data-wishlist-item]'));
    announce(mount.dataset.updatedText);
    return true;
  } catch (error) {
    if (error.name === 'AbortError') return 'aborted';
    const failure = document.createElement('div');
    failure.className = 'ca-wishlist__empty';
    failure.setAttribute('role', 'status');

    const message = document.createElement('p');
    message.textContent = mount.dataset.errorText || '';
    failure.append(message);

    if (mount.dataset.retryText) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary';
      retry.textContent = mount.dataset.retryText;
      retry.addEventListener('click', () => {
        void loadPanel({ listId, page, handles });
      });
      failure.append(retry);
    }

    mount.replaceChildren(failure);
    announce(mount.dataset.errorText);
    return false;
  } finally {
    if (inFlight === controller) {
      inFlight = null;
      mount.removeAttribute('aria-busy');
    }
  }
}

function renderInitialPanel() {
  const mount = getMount();
  if (!document.getElementById('wishlist-state') || !mount) return;

  if (isLoggedIn()) {
    if (mount.dataset.deferred !== 'true') return;
    const prefetch = window.__caWishlistPrefetch;
    void loadPanel(prefetch && !prefetch.handles ? { prefetched: prefetch.promise } : {});
    return;
  }

  const handles = guestHandles();
  if (!handles.length) return;

  const prefetch = window.__caWishlistPrefetch;
  const reusable = prefetch && String(prefetch.handles) === String(handles);
  void loadPanel(reusable ? { handles, prefetched: prefetch.promise } : { handles });
}

function watchPanelControls() {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const mount = getMount();
    if (!mount) return;

    const pager = event.target.closest('[data-wishlist-page]');
    if (pager) {
      event.preventDefault();
      const opts = { page: Number(pager.dataset.wishlistPage), listId: mount.dataset.listId };
      void loadPanel(isLoggedIn() ? opts : { ...opts, handles: guestHandles() });
      return;
    }

    const tab = event.target.closest('[data-wishlist-tab]');
    if (!tab) return;

    event.preventDefault();
    const tabs = [...document.querySelectorAll('[data-wishlist-tab]')];
    const previous = tabs.find((t) => t.getAttribute('aria-pressed') === 'true');
    const setActive = (active) => {
      for (const t of tabs) {
        t.classList.toggle('is-active', t === active);
        t.setAttribute('aria-pressed', String(t === active));
      }
      const host = document.querySelector('wishlist-account');
      if (host) {
        host.dataset.listName = active?.dataset.wishlistTabName ?? '';
        host.dataset.listMessage = active?.dataset.wishlistTabMessage ?? '';
        const output = host.querySelector('.ca-wishlist__share-output');
        if (output) output.textContent = '';
        const panel = host.querySelector('[data-share-panel]');
        if (panel instanceof HTMLElement) panel.hidden = true;
        const reset = host.querySelector('[data-share-reset]');
        if (reset instanceof HTMLElement) reset.hidden = true;
      }
    };

    setActive(tab);
    loadPanel({ listId: tab.dataset.wishlistTab, page: 1 }).then((ok) => {
      if (ok === false && previous) setActive(previous);
    });
  });
}

function dropCard(key) {
  const mount = getMount();
  const card = mount?.querySelector(`[data-wishlist-item="${key}"]`);
  if (!card) return;

  const grid = card.parentElement;
  if (card.contains(document.activeElement)) {
    const next = card.nextElementSibling ?? card.previousElementSibling;
    const target =
      next?.querySelector('button, a') ?? document.querySelector('.ca-wishlist__heading, .ca-page-hero__title');
    if (target) {
      if (target.tagName === 'H1' && !target.hasAttribute('tabindex')) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    }
  }

  card.remove();
  announce(mount.dataset.removedText);
  if (grid?.querySelector('[data-wishlist-item]')) return;

  const stillHasItems = isLoggedIn() ? totalCount() > 0 : guestHandles().length > 0;
  if (!stillHasItems) {
    showEmptyState();
    return;
  }

  // Only a paginated list needs re-fetching, to pull the next page's first item forward.
  if (!mount.querySelector('[data-wishlist-page]')) return;

  if (isLoggedIn()) {
    void loadPanel({ listId: mount.dataset.listId, page: Number(mount.dataset.page) || 1 });
  } else {
    void loadPanel({ handles: guestHandles() });
  }
}

function showEmptyState() {
  const mount = getMount();
  const template = document.querySelector('[data-wishlist-empty-template]');
  if (!mount || !template) return;

  mount.replaceChildren(template.content.cloneNode(true));
  setToolbarVisible(false);
}

/**
 * Share and Rename follow whether there is a list to act on.
 *
 * @param {boolean} visible
 */
function setToolbarVisible(visible) {
  document.querySelector('wishlist-account')?.toggleAttribute('hidden', !visible);
  for (const tabs of document.querySelectorAll('.ca-wishlist__tabs')) {
    tabs.toggleAttribute('hidden', !visible);
  }
}

function watchMerge() {
  document.addEventListener(ThemeEvents.wishlistUpdate, (event) => {
    if (!event.detail?.merged) return;
    const mount = getMount();
    if (!mount) return;
    void loadPanel({ listId: mount.dataset.listId, page: 1 });
  });
}

function watchRemovals() {
  document.addEventListener(ThemeEvents.wishlistUpdate, (event) => {
    const { key, inList, listId } = event.detail ?? {};
    if (inList !== false || !key) return;

    const mount = getMount();
    if (!mount) return;
    if (isLoggedIn() && listId && mount.dataset.listId && listId !== mount.dataset.listId) return;

    dropCard(key);
  });
}

function watchLastPageRequests() {
  document.addEventListener(ThemeEvents.wishlistShowLastPage, (event) => {
    const mount = getMount();
    if (!mount || !isLoggedIn()) return;
    const last = Number(mount.dataset.lastPage) || 1;
    if (last <= (Number(mount.dataset.page) || 1)) return;
    event.preventDefault();
    void loadPanel({ listId: mount.dataset.listId, page: last });
  });
}

renderInitialPanel();
watchMerge();
watchLastPageRequests();
watchRemovals();
watchPanelControls();

class WishlistAccount extends Component {
  requiredRefs = ['shareButton', 'shareOutput'];
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.#dialog?.addEventListener('close', this.#onDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  get #dialog() {
    return this.querySelector('[data-rename-dialog]');
  }

  #say(message) {
    const region = this.querySelector('[data-rename-live]');
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  #onClick = (event) => {
    if (event.target.closest('[data-rename-cancel]')) {
      event.preventDefault();
      this.#closeRename();
      return;
    }
    if (event.target.closest('[data-rename-open]')) {
      event.preventDefault();
      this.#openRename();
      return;
    }
    if (event.target.closest('[data-share-copy]')) {
      event.preventDefault();
      void this.#copy(this.refs.shareOutput?.textContent ?? '');
      return;
    }
    if (event.target.closest('[data-share-reset]')) {
      event.preventDefault();
      if (window.confirm(this.dataset.resetConfirmText || '')) void this.share({ reset: true });
    }
  };

  #onDialogClose = () => {
    this.querySelector('[data-rename-open]')?.focus();
  };

  #openRename() {
    const dialog = this.#dialog;
    const field = dialog?.querySelector('[data-rename-field]');
    if (!dialog || !field) return;

    const current = this.dataset.listName ?? '';
    field.value = current;
    const message = dialog.querySelector('[data-message-field]');
    if (message) message.value = this.dataset.listMessage ?? '';
    this.#showError('');
    dialog.showModal();
    field.focus();
    field.setSelectionRange(current.length, current.length);
  }

  #closeRename() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
    else this.#onDialogClose();
  }

  #showError(message) {
    const box = this.querySelector('[data-rename-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  #onSubmit = (event) => {
    const form = event.target.closest('[data-rename-form]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    const message = form.querySelector('[data-message-field]')?.value;
    void this.#rename(
      form.querySelector('[data-rename-field]')?.value ?? '',
      message === undefined || message.trim() === (this.dataset.listMessage ?? '').trim() ? undefined : message
    );
  };

  async #rename(raw, message) {
    const name = sanitizeListName(raw);
    // The Worker rejects an empty name, so don't send a request that can only 400.
    if (!name) {
      this.#showError(this.dataset.renameErrorText || '');
      return;
    }

    const save = this.querySelector('[data-rename-save]');
    save?.setAttribute('aria-busy', 'true');
    save?.setAttribute('aria-disabled', 'true');

    try {
      const listId = getMount()?.dataset.listId || this.dataset.listId || 'default';
      const result = await renameList(listId, name, message);
      this.#applyName(result.name ?? name);
      if (typeof result.message === 'string') this.#applyMessage(result.message);
      this.#closeRename();
      this.#say(this.dataset.renamedText);
    } catch (error) {
      // A lapsed session is not a bad name; don't send them rewriting a valid one.
      if (error?.code === 'login_required') {
        markLoggedOut();
        this.#showError(translated(this.dataset.sessionErrorText) || this.dataset.renameErrorText || '');
      } else {
        this.#showError(this.dataset.renameErrorText || '');
      }
    } finally {
      save?.removeAttribute('aria-busy');
      save?.removeAttribute('aria-disabled');
    }
  }

  #applyMessage(message) {
    this.dataset.listMessage = message;
    const listId = getMount()?.dataset.listId || this.dataset.listId || 'default';
    const tab = document.querySelector(`[data-wishlist-tab="${CSS.escape(listId)}"]`);
    if (tab instanceof HTMLElement) tab.dataset.wishlistTabMessage = message;
  }

  /** Mirrors the list name into the label, share sheet title, and tab. */
  #applyName(name) {
    this.dataset.listName = name;

    const label = this.querySelector('[data-list-name-text]');
    if (label) label.textContent = name;

    const listId = getMount()?.dataset.listId || this.dataset.listId || 'default';
    const tab = document.querySelector(`[data-wishlist-tab="${CSS.escape(listId)}"]`);
    if (tab) {
      tab.dataset.wishlistTabName = name;
      const count = tab.querySelector('.ca-wishlist__tab-count');
      tab.textContent = name;
      if (count) tab.append(' ', count);
    }
  }

  #sharing = false;

  #toggleShareActions(visible) {
    const actions = this.querySelector('.ca-wishlist__share-panel-actions');
    if (actions instanceof HTMLElement) actions.hidden = !visible;
  }

  #setStatus(message) {
    const status = this.querySelector('[data-share-status]');
    if (status) status.textContent = message || '';
  }

  async #copy(url) {
    if (!url) return false;
    const copied = navigator.clipboard?.writeText
      ? await navigator.clipboard
          .writeText(url)
          .then(() => true)
          .catch(() => false)
      : false;
    if (copied) {
      this.#setStatus(translated(this.dataset.copiedText));
      this.#say(this.dataset.copiedText);
    } else {
      this.#setStatus('');
      this.#selectOutput();
      this.#say(url);
    }
    return copied;
  }

  #selectOutput() {
    const output = this.refs.shareOutput;
    const selection = window.getSelection();
    if (!output || !selection) return;
    const range = document.createRange();
    range.selectNodeContents(output);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  async share({ reset = false } = {}) {
    const { shareButton, shareOutput } = this.refs;
    if (!shareButton || !shareOutput || this.#sharing) return;
    const resetButton = this.querySelector('[data-share-reset]');
    this.#sharing = true;
    if (resetButton instanceof HTMLButtonElement) resetButton.disabled = true;

    shareButton.setAttribute('aria-disabled', 'true');
    shareButton.setAttribute('aria-busy', 'true');

    try {
      const listId = getMount()?.dataset.listId || 'default';
      const { url } = await createShare(listId, { reset });
      if ((getMount()?.dataset.listId || 'default') !== listId) return;
      shareOutput.textContent = url;
      const panel = this.querySelector('[data-share-panel]');
      if (panel instanceof HTMLElement) panel.hidden = false;
      this.#toggleShareActions(true);
      const preview = this.querySelector('[data-share-preview]');
      if (preview instanceof HTMLAnchorElement) preview.href = url;
      if (resetButton instanceof HTMLElement) resetButton.hidden = false;
      this.#setStatus('');
      if (reset) {
        this.#setStatus(translated(this.dataset.resetDoneText));
        this.#say(this.dataset.resetDoneText);
        return;
      }

      const touch = window.matchMedia('(pointer: coarse)').matches;
      let outcome = 'unavailable';
      if (touch && navigator.share) {
        const message = this.dataset.listMessage || '';
        outcome = await navigator
          .share({ title: this.dataset.listName, ...(message ? { text: message } : {}), url })
          .then(() => 'shared')
          .catch((error) => (error?.name === 'AbortError' ? 'cancelled' : 'unavailable'));
      }
      if (outcome === 'unavailable') await this.#copy(url);
    } catch (_) {
      const panel = this.querySelector('[data-share-panel]');
      if (panel instanceof HTMLElement) panel.hidden = false;
      this.#toggleShareActions(false);
      shareOutput.textContent = this.dataset.errorText || '';
      this.#say(this.dataset.errorText);
    } finally {
      shareButton.removeAttribute('aria-disabled');
      shareButton.removeAttribute('aria-busy');
      if (resetButton instanceof HTMLButtonElement) resetButton.disabled = false;
      this.#sharing = false;
    }
  }
}

if (!customElements.get('wishlist-account')) {
  customElements.define('wishlist-account', WishlistAccount);
}
